import { useMemo } from "react";
import {
  RANK_TIERS,
  ROMAN,
  getTier,
  isStarTier,
  tierFromStars,
  rankLabelOf,
  reputationLevel,
} from "../../utils/rank";
import "./rankEditor.css";

const DIVISION_TIERS = RANK_TIERS.filter((t) => t.divisions);
const STAR_TIERS = RANK_TIERS.filter((t) => t.starMin !== undefined);

/**
 * Ô nhập rank + uy tín cho acc Liên Quân.
 *
 * Hai chế độ tách bạch để không nhập được dữ liệu vô nghĩa:
 *  - "Theo đoàn": chọn bậc + đoàn (số đoàn đổi theo bậc: Đồng/Bạc 3, Vàng 4, còn lại 5).
 *  - "Theo sao": chỉ nhập số sao, bậc tự suy ra — đúng cách game xếp hạng từ Cao Thủ trở lên.
 *
 * value: { rank: {tier, division, stars} | null, reputation: number | null }
 */
export default function RankEditor({ value, onChange }) {
  const rank = value?.rank || null;
  const reputation = value?.reputation;
  const mode = rank?.tier && isStarTier(rank.tier) ? "stars" : "divisions";
  const tier = rank?.tier ? getTier(rank.tier) : null;

  const repLevel = useMemo(() => reputationLevel(reputation), [reputation]);
  const repNumber = reputation === null || reputation === undefined || reputation === "" ? "" : reputation;

  const emit = (patch) => onChange({ rank, reputation, ...patch });

  const setMode = (nextMode) => {
    if (nextMode === mode) return;
    emit({
      rank:
        nextMode === "stars"
          ? { tier: tierFromStars(0), division: null, stars: 0 }
          : { tier: "dong", division: 3, stars: null },
    });
  };

  const setTier = (tierKey) => {
    if (!tierKey) return emit({ rank: null });
    const next = getTier(tierKey);
    if (!next) return;
    // Giữ đoàn cũ nếu bậc mới còn đoàn đó, ngược lại rơi về đoàn thấp nhất.
    const division = Math.min(rank?.division || next.divisions, next.divisions);
    emit({ rank: { tier: next.key, division, stars: null } });
  };

  const setDivision = (division) => {
    if (!tier) return;
    emit({ rank: { tier: tier.key, division: Number(division), stars: null } });
  };

  const setStars = (raw) => {
    const stars = raw === "" ? 0 : Math.max(0, Math.floor(Number(raw) || 0));
    emit({ rank: { tier: tierFromStars(stars), division: null, stars } });
  };

  const setReputation = (raw) => {
    if (raw === "") return emit({ reputation: null });
    const n = Math.min(100, Math.max(0, Math.round(Number(raw) || 0)));
    emit({ reputation: n });
  };

  return (
    <div className="rank-editor">
      {/* Không đặt tiêu đề ở đây: cả Write.jsx lẫn SinglePost.jsx đều đã có
          nhãn/header "Rank & uy tín" bao ngoài, thêm nữa là lặp tiêu đề. */}
      <div className="rank-editor__toolbar">
        <div className="rank-editor__modes" role="group" aria-label="Cách xếp hạng">
          <button
            type="button"
            className={`rank-editor__mode ${mode === "divisions" ? "active" : ""}`}
            onClick={() => setMode("divisions")}
          >
            Theo đoàn
          </button>
          <button
            type="button"
            className={`rank-editor__mode ${mode === "stars" ? "active" : ""}`}
            onClick={() => setMode("stars")}
          >
            Theo sao
          </button>
        </div>
        {rank?.tier && (
          <button type="button" className="rank-editor__clear" onClick={() => emit({ rank: null })}>
            <i className="fas fa-times"></i> Xoá rank
          </button>
        )}
      </div>

      {mode === "divisions" ? (
        <div className="rank-editor__row">
          <label className="rank-editor__field">
            <span className="rank-editor__label">Bậc</span>
            <select
              className="rank-editor__input"
              value={tier?.key || ""}
              onChange={(e) => setTier(e.target.value)}
            >
              <option value="">— Chưa xếp hạng —</option>
              {DIVISION_TIERS.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>

          <label className="rank-editor__field">
            <span className="rank-editor__label">Đoàn</span>
            <select
              className="rank-editor__input"
              value={rank?.division || ""}
              disabled={!tier}
              onChange={(e) => setDivision(e.target.value)}
            >
              {tier &&
                // Đổ từ đoàn thấp nhất (V) lên đoàn I cho khớp cách game hiển thị
                Array.from({ length: tier.divisions }, (_, i) => tier.divisions - i).map((d) => (
                  <option key={d} value={d}>
                    Đoàn {ROMAN[d]}
                  </option>
                ))}
            </select>
          </label>
        </div>
      ) : (
        <div className="rank-editor__row">
          <label className="rank-editor__field">
            <span className="rank-editor__label">Số sao</span>
            <input
              type="number"
              className="rank-editor__input"
              min="0"
              step="1"
              value={rank?.stars ?? 0}
              onChange={(e) => setStars(e.target.value)}
            />
          </label>
          <div className="rank-editor__field">
            <span className="rank-editor__label">Bậc (tự suy ra)</span>
            <div className="rank-editor__derived">{tier?.label || "—"}</div>
          </div>
        </div>
      )}

      {mode === "stars" && (
        <ul className="rank-editor__hint">
          {STAR_TIERS.map((t) => (
            <li key={t.key}>
              <b>{t.label}</b>: {t.starMax === null ? `${t.starMin}+ sao` : `${t.starMin}–${t.starMax} sao`}
            </li>
          ))}
        </ul>
      )}

      {tier?.note && <p className="rank-editor__note">{tier.note}</p>}

      <div className="rank-editor__rep">
        <div className="rank-editor__rep-head">
          <span className="rank-editor__label">Uy tín (0-100)</span>
          {repLevel && (
            <span className="rank-editor__rep-level" style={{ color: repLevel.color }}>
              {repLevel.label}
            </span>
          )}
        </div>
        <div className="rank-editor__rep-controls">
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={repNumber === "" ? 0 : repNumber}
            onChange={(e) => setReputation(e.target.value)}
            className="rank-editor__range"
            style={{ accentColor: repLevel?.color || "#9fb0c4" }}
          />
          <input
            type="number"
            className="rank-editor__input rank-editor__rep-number"
            min="0"
            max="100"
            step="1"
            placeholder="—"
            value={repNumber}
            onChange={(e) => setReputation(e.target.value)}
          />
        </div>
        <p className="rank-editor__note">Để trống = chưa đánh giá uy tín (khác với uy tín 0).</p>
      </div>

      <div className="rank-editor__preview">
        <span className="rank-editor__preview-label">Xem trước:</span>
        <span
          className="rank-editor__badge"
          style={{ borderColor: tier?.color || "#3a4658", color: tier?.color || "#9fb0c4" }}
        >
          {rankLabelOf(rank) || "Chưa xếp hạng"}
        </span>
        {repNumber !== "" && (
          <span className="rank-editor__badge" style={{ borderColor: repLevel?.color, color: repLevel?.color }}>
            Uy tín {repNumber}
          </span>
        )}
      </div>
    </div>
  );
}
